/** @format */

import React, { useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import {
  PageContainer,
  ButtonLoading,
  SimpleDialog,
  LoadingPage,
  StepperKeeper,
  Icons,
  PriceLabel,
} from 'components';
import { validator } from 'myValidation';
import { animateScroll as scroll } from 'react-scroll';
import { useForm, FormProvider } from 'react-hook-form';
import persianJs from 'persianjs';
import { constBreadCrumbsTitle } from 'utility';
import { useNavigate } from 'react-router-dom';
import createToast from 'components/Toast/Toast';
import useCustomHook from './hooks';
import { useCurrencyUnit } from 'hooks';

import {
  Grid,
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Button,
} from '@mui/material';
import { useBusiness } from 'hooks';
import CartableDefineDelegationServices from './services/CartableDefineDelegationServices';
import { BaseInfo, Constraints } from './components/index';

export const USE_PERIODS = [
  { value: 'DAY_PERIOD', label: 'روز' },
  { value: 'MONTH_PERIOD', label: 'ماه' },
  { value: 'YEAR_PERIOD', label: 'سال' },
];
const deleteNullValueFromObj = (obj) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] === null || obj[key] === '') {
      delete obj[key];
    }
  });
};
const CartableDefineDelegation = () => {
  const navigate = useNavigate();
  const business = useBusiness();
  const currencyText = useCurrencyUnit(false);

  // -- STATES --//
  // type of cash [حساب دیجیتال - حساب های تفصیلی]
  const {
    expireDate,
    setExpireDate,
    startDate,
    setStartDate,
    name,
    setName,
    rowsWithError,
    setRowsWithError,
    isResultErrorsOpen,
    setIsResultErrorsOpen,
    guilds,
    setGuilds,
    usages,
    setUsages,
    selectedProducts,
    setSelectedProducts,
    usageEnabled,
    setUsageEnabled,
    periodicEnabled,
    setPeriodicEnabled,
    usageLimitsEnabled,
    setUsageLimitsEnabled,
    usagePeriodEnabled,
    setUsagePeriodEnabled,
    foundUser,
    setFoundUser,
    loading,
    setLoading,
    submitting,
    setSubmitting,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    activeStep,
    setActiveStep,
    typeOfMethod,
    setTypeOfMethod,
  } = useCustomHook();

  // -- HOOKS --//
  const methods = useForm();
  const { handleSubmit, reset, formState: { errors }, watch, getValues, setValue } = methods;
  const classes = useStyles();

  useEffect(() => {
    setValue('usePeriod', null);
  }, []);

  useEffect(() => {
    methods.register('usePeriod');
    methods.register('usePeriodCount');

    return () => {
      methods.unregister('usePeriod');
      methods.unregister('usePeriodCount');
    };
  }, [methods.register, methods.unregister]);

  useEffect(() => {
    if (!watch('usePeriod')) {
      setValue('usePeriodCount', '');
    }
  }, [watch('usePeriod')]);

  // -- API REQUESTS --//
  const onTransfer = async () => {
    if (submitting) return;

    // usage limits count and period
    let maxTryCount = null;
    let usePeriod = null;
    let usePeriodCount = null;

    if (usageLimitsEnabled) {
      maxTryCount = getValues('maxTryCount') ? parseInt(getValues('maxTryCount')) : null;
      usePeriod = usagePeriodEnabled ? getValues('usePeriod') : 'NO_PERIOD';
      usePeriodCount =
        !getValues('usePeriod') || !usagePeriodEnabled
          ? null
          : getValues('usePeriodCount')
          ? parseInt(getValues('usePeriodCount'))
          : null;
    }

    setSubmitting(true);
    try {
      const body = {
        name: getValues('name'),
        amount: getValues('amount'),
        description: getValues('description'),
        userId: foundUser?.linkedUser?.id,
        currencyCode: getValues('guildCode')?.currency?.code ?? 'IRR',
        uniqueId: getValues('uniqueId'),
        cartableId: navigate.location?.state?.group?.multiConfirmationSrv?.id,
        // date limits
        startDate: startDate
          ? persianJs(`${startDate.format('jYYYY/jMM/jDD 00:00')}`)
              .persianNumber()
              .toString()
          : null,
        expireDate: expireDate
          ? persianJs(`${expireDate.format('jYYYY/jMM/jDD 23:59')}`)
              .persianNumber()
              .toString()
          : null,

        // if usageEnabled
        permittedGuildCode: usageEnabled
          ? guilds.length > 0
            ? guilds.map((v) => v?.code)
            : []
          : [],
        permittedBusinessId: usageEnabled
          ? usages.length > 0
            ? usages.map((v) => v?.id)
            : []
          : [],
        permittedProductId:
          usageEnabled && selectedProducts
            ? selectedProducts.length > 0
              ? selectedProducts.map((v) => v?.id)
              : []
            : [],

        // if usageLimitsEnabled
        maxTryCount: usageLimitsEnabled ? maxTryCount : null,
        usePeriod: usageLimitsEnabled && usagePeriodEnabled ? usePeriod : null,
        usePeriodCount: usageLimitsEnabled && usagePeriodEnabled ? usePeriodCount : null,

        // if periodicEnabled
        periodCode: periodicEnabled ? getValues('periodCode') ?? 'NO_PERIOD' : 'NO_PERIOD',
        periodCount: periodicEnabled ? getValues('periodCount') ?? 0 : 0,
      };
      if (typeOfMethod === 'wallet') {
        body.wallet = navigate.location?.state?.group?.multiConfirmationSrv?.wallet?.code;
      } else {
        body.guildCode = navigate.location?.state?.group?.multiConfirmationSrv?.guildSrv?.code;
      }
      deleteNullValueFromObj(body);
      // Handle File Form Data
      await CartableDefineDelegationServices.transfer(body);
      handleSubmitEnd();
      scroll.scrollToTop();
      createToast('موفق', 'تفویض وجه با موفقیت انجام شد!', 'success');

      navigate('/CartableRequest/List');
      // navigate(-1);
    } catch (error) {
      const errMsg = error?.response?.data?.message?.fa;
      if (error?.response?.status === 415) {
        if (errMsg) {
          // "$" sign as seperator of multiple messages
          const errs = errMsg.split('$');
          setRowsWithError(errs);
          setIsConfirmModalOpen(false);
          return setIsResultErrorsOpen(true);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCheckContact = async (data) => {
    try {
      if (
        typeOfMethod === 'guild' &&
        !navigate.location.state.group?.multiConfirmationSrv?.guildSrv?.code
      ) {
      }

      const body = {
        firstName: data?.contactInfo,
        lastName: data?.contactInfo,
      };
      if (validator.isEmail(data?.contactInfo ?? '')) {
        body.email = data?.contactInfo;
      } else if (validator.isMobilePhone(data?.contactInfo ?? '')) {
        body.cellphoneNumber = data?.contactInfo;
      } else if (validator.matches(data?.contactInfo ?? '', /([A-Za-z0-9._-]){3,63}/)) {
        body.username = data?.contactInfo;
      } else {
        return;
      }
      setSubmitting(true);
      const r = await CartableDefineDelegationServices.checkContact(body);
      if (r.result?.length > 0) {
        if (r.result[0]?.linkedUser?.id === business?.userId) {
          setSubmitting(false);
          return;
        } else if (!r.result[0]?.linkedUser) {
          setSubmitting(false);
          return;
        }
        setFoundUser(r.result[0]);
      } else {
        setSubmitting(false);
      }
      setIsConfirmModalOpen(true);
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  };

  // -- OTHER FUNCTIONS --//
  const handleChangeExpire = (e) => {
    setExpireDate(e);
  };

  const handleChangeStart = (e) => {
    setStartDate(e);
    setExpireDate(null);
  };

  const handleSubmitEnd = () => {
    setIsConfirmModalOpen(false);
    setFoundUser(null);
    reset({
      contactInfo: null,
      amount: null,
      description: null,
      usageEnabled: null,
      guildUsage: null,
      bizUsage: null,
      periodicEnabled: null,
      periodCount: null,
      periodCode: null,
    });
    setGuilds([]);
    setUsages([]);
    setUsageEnabled(false);
    setPeriodicEnabled(false);
    setExpireDate(null);
    // getBalance();
  };

  const handleCloseModal = () => {
    setIsConfirmModalOpen(false);
    setFoundUser(null);
  };

  const getPeriodLabel = (item) => {
    const items = {
      DAY_PERIOD: 'روز',
      MONTH_PERIOD: 'ماه',
      YEAR_PERIOD: 'سال',
    };
    return item ? items[item] : null;
  };

  const handleCloseErrorsModal = () => {
    setIsResultErrorsOpen(false);
    setIsConfirmModalOpen(false);
    setRowsWithError([]);
  };
  const hintText =
    'در این بخش شما می‌توانید وجه حساب دیجیتال خود را به کاربر دیگر(عضو پاد) با تعریف محدودیت زمانی و اعمال محدودیت روی محل مصرف تفویض کنید. در صورت تفویض وجه، تا زمانی که مبلغ تفویض شده، مصرف نشده باشد، وجه از حساب دیجیتال شما کسر نمی‌شود.';

  return (
    <PageContainer
      title="تفویض وجه"
      className={classes.root}
      description={hintText}
      breadcrumbs={constBreadCrumbsTitle.CARTABLE_DELEGATION}
    >
      {loading || !business?.userId ? <LoadingPage /> : null}

      <SimpleDialog
        title="خطا در تفویض وجه"
        icon={<Icons icon="WarningTriangle" color="#F44336" size="22" />}
        open={isResultErrorsOpen}
        actions={
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: -8 }}
            onClick={handleCloseErrorsModal}
          >
            بستن
          </Button>
        }
        onClose={handleCloseErrorsModal}
      >
        <Typography className={classes.alertTitle}>
          تفویض وجه با خطا مواجه شد. موارد زیر را اصلاح کنید و دوباره تلاش کنید:
        </Typography>
        {rowsWithError?.map((v, k) => (
          <li key={k}>{v}</li>
        ))}
      </SimpleDialog>

      {!loading && business?.userId ? (
        <>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onCheckContact)} className={classes.form}>
              <StepperKeeper
                titles={[{ label: 'اطلاعات کلی', liner: true }, { label: 'محدودیت‌ها' }]}
                onStepChanged={setActiveStep}
                forceSelectedTabIndex={activeStep}
                stepsToHideNext={'false'}
                panels={[
                  <BaseInfo
                    startDate={startDate}
                    handleChangeStart={handleChangeStart}
                    expireDate={expireDate}
                    handleChangeExpire={handleChangeExpire}
                    setTypeOfMethod={setTypeOfMethod}
                    typeOfMethod={typeOfMethod}
                    name={name}
                    setName={setName}
                  />,
                  <Constraints
                    usageEnabled={usageEnabled}
                    setUsageEnabled={setUsageEnabled}
                    guilds={guilds}
                    setGuilds={setGuilds}
                    usages={usages}
                    setUsages={setUsages}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                    usageLimitsEnabled={usageLimitsEnabled}
                    setUsageLimitsEnabled={setUsageLimitsEnabled}
                    usagePeriodEnabled={usagePeriodEnabled}
                    setUsagePeriodEnabled={setUsagePeriodEnabled}
                    periodicEnabled={periodicEnabled}
                    setPeriodicEnabled={setPeriodicEnabled}
                  />,
                ]}
                formAction={
                  <Grid container item xs={12} justifyContent="flex-end">
                    <div className={classes.containButton}>
                      <ButtonLoading
                        variant="contained"
                        color="primary"
                        loading={submitting}
                        name="بررسی اطلاعات"
                        type="submit"
                      />
                    </div>
                  </Grid>
                }
              />
            </form>
          </FormProvider>
        </>
      ) : null}

      {/* Confirm */}
      <SimpleDialog
        title="تخصیص اعتبار با جزئیات زیر انجام گیرد؟"
        open={isConfirmModalOpen}
        icon={<Icons icon="coin" color="#000" size="25" />}
        onClose={handleCloseModal}
        actions={
          <>
            <ButtonLoading
              name="تایید"
              onClick={onTransfer}
              loading={submitting}
              variant="contained"
              color="primary"
            />

            <Button onClick={handleCloseModal} variant="contained">
              انصراف
            </Button>
          </>
        }
      >
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} aria-label="simple table">
            <TableBody>
              {
                <>
                  <TableRow>
                    <TableCell className={classes.titleCell} component="th" scope="row">
                      نام
                    </TableCell>
                    <TableCell>
                      {foundUser?.linkedUser?.name ??
                        `${!foundUser?.firstName && !foundUser?.lastName ? '-' : ''}${
                          foundUser?.firstName ?? ''
                        } ${foundUser?.lastName ?? ''}`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.titleCell} component="th" scope="row">
                      میزان اعتبار
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.boldCell}>
                        <PriceLabel amount={watch('amount')} />
                        <span className={classes.currencyText}> {currencyText}</span>
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.titleCell} component="th" scope="row">
                      تاریخ انقضا
                    </TableCell>
                    <TableCell>
                      <Typography className={classes.boldCell}>
                        {expireDate ? expireDate.format('23:59 jYYYY/jMM/jDD') : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              }
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  شرح
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {watch('description')?.length > 0 ? watch('description') : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  محدودیت محل مصرف
                  <br />
                  (حساب‌های تفصیلی)
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {guilds.length > 0
                      ? guilds.map((v, k) => (
                          <span key={k}>{`${v.name}${k < guilds.length - 1 ? '،' : ''} `}</span>
                        ))
                      : 'ندارد'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  محدودیت محل مصرف
                  <br />
                  (کسب‌وکارها)
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {usages.length > 0
                      ? usages.map((v, k) => (
                          <span key={k}>{`${v.name}${k < usages.length - 1 ? '،' : ''} `}</span>
                        ))
                      : 'ندارد'}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  محدودیت میزان مصرف
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {!watch('maxTryCount') && !watch('usePeriod') ? 'ندارد' : ''}
                    {watch('maxTryCount') ? `${watch('maxTryCount')} مرتبه` : ''}
                    {watch('maxTryCount') && watch('usePeriod') && watch('usePeriodCount')
                      ? ' در هر '
                      : ''}
                    {watch('usePeriod') && watch('usePeriodCount')
                      ? `${watch('usePeriodCount')} ${
                          USE_PERIODS.find((v) => v.value === watch('usePeriod'))?.label ?? ''
                        }`
                      : ''}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.titleCell} component="th" scope="row">
                  تمدید خودکار دوره‌ای
                </TableCell>
                <TableCell>
                  <Typography className={classes.boldCell}>
                    {watch('periodCode') && watch('periodCode')
                      ? `هر ${watch('periodCount')} ${getPeriodLabel(watch('periodCode'))} یک بار`
                      : 'ندارد'}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SimpleDialog>
      {/* End Confirm */}
    </PageContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiRadio-colorSecondary.Mui-checked': {
      color: theme.palette.primary.main,
    },
    '& textarea': {
      minHeight: 80,
    },
  },
  alertTitle: {
    fontWeight: 'bold',
    fontSize: 14,
    paddingBottom: theme.spacing(0.5),
  },
}));

export default CartableDefineDelegation;
